import { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import {
  computeMatrixDiff,
  emptyMatrix,
  matrixFromPermissions,
  matrixToPermissions,
  slugifyRoleCode,
  summarizeDiff,
  validateRoleName,
  type MatrixState,
  type RoleDetail,
  type RoleSummary,
} from '@/pages/company/roles/rolesLogic';
import {
  useCreateRole,
  useRoleDetail,
  useRoleMembers,
  useUpdateRole,
} from '@/pages/company/roles/hooks/useRolesQueries';
import { PermissionMatrix, ScopeLegend } from './PermissionMatrix';
import { DiffConfirmDialog } from './DiffConfirmDialog';

/**
 * Режим редактора:
 *   - { kind: 'edit', roleId }           — правка кастомной роли (диф перед save)
 *   - { kind: 'create' }                 — новая роль с нуля (пустая матрица)
 *   - { kind: 'duplicate-custom', from } — копия существующей роли (напр. системной),
 *       матрица предзаполнена, имя пустое, создаётся как новая кастомная (POST /roles)
 */
export type EditorMode =
  | { kind: 'edit'; roleId: string }
  | { kind: 'create' }
  | { kind: 'duplicate-custom'; from: RoleDetail };

interface RoleEditorDrawerProps {
  mode: EditorMode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Кастомные роли тенанта — для проверки дубля имени. */
  customRoles: RoleSummary[];
}

/**
 * RoleEditorDrawer — редактор кастомной роли (AK-343, фреймы 2/4-дубль).
 *
 * Управляет именем/описанием/матрицей. Валидация имени инлайн (code-collision
 * #402). Сохранение правки существующей роли → сперва DiffConfirmDialog
 * (что изменится + кого затронет). Создание/дублирование → сразу POST.
 */
export function RoleEditorDrawer({
  mode,
  open,
  onOpenChange,
  customRoles,
}: RoleEditorDrawerProps) {
  const isEdit = mode?.kind === 'edit';
  const editId = isEdit ? mode.roleId : null;

  const detailQ = useRoleDetail(editId);
  const membersQ = useRoleMembers(editId);
  const createMut = useCreateRole();
  const updateMut = useUpdateRole();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [matrix, setMatrix] = useState<MatrixState>(emptyMatrix);
  const [baseline, setBaseline] = useState<MatrixState>(emptyMatrix);
  const [nameError, setNameError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [diffOpen, setDiffOpen] = useState(false);

  // Инициализация формы под режим.
  useEffect(() => {
    if (!open || !mode) return;
    if (mode.kind === 'create') {
      setName('');
      setDescription('');
      setMatrix(emptyMatrix());
      setBaseline(emptyMatrix());
    } else if (mode.kind === 'duplicate-custom') {
      const m = matrixFromPermissions(mode.from.permissions);
      setName('');
      setDescription(mode.from.description ?? '');
      setMatrix(m);
      setBaseline(emptyMatrix()); // для копии дифа нет — всё «новое»
    }
    // edit: инициализируется из detailQ ниже.
    setNameError(undefined);
    setSubmitError(undefined);
    setDiffOpen(false);
  }, [open, mode]);

  useEffect(() => {
    if (open && isEdit && detailQ.data) {
      const m = matrixFromPermissions(detailQ.data.permissions);
      setName(detailQ.data.name);
      setDescription(detailQ.data.description ?? '');
      setMatrix(m);
      setBaseline(m);
    }
  }, [open, isEdit, detailQ.data]);

  const existingCustom = customRoles;
  const currentId = editId;

  const diff = useMemo(() => computeMatrixDiff(baseline, matrix), [baseline, matrix]);
  const diffLines = useMemo(() => summarizeDiff(diff), [diff]);

  const dirty =
    mode?.kind === 'edit'
      ? name !== (detailQ.data?.name ?? '') ||
        description !== (detailQ.data?.description ?? '') ||
        diff.hasChanges
      : name.trim().length > 0;

  if (!mode) return null;

  const title =
    mode.kind === 'edit'
      ? 'Редактор роли'
      : mode.kind === 'duplicate-custom'
        ? 'Новая роль (копия)'
        : 'Новая роль';

  const runNameValidation = (value: string): boolean => {
    const v = validateRoleName(value, [], currentId, existingCustom);
    setNameError(v.error);
    return v.ok;
  };

  const doSave = async () => {
    setSubmitError(undefined);
    const permissions = matrixToPermissions(matrix);
    try {
      if (mode.kind === 'edit') {
        await updateMut.mutateAsync({
          id: mode.roleId,
          payload: { name: name.trim(), description: description.trim() || null, permissions },
        });
      } else {
        await createMut.mutateAsync({
          code: slugifyRoleCode(name),
          name: name.trim(),
          description: description.trim() || null,
          permissions,
        });
      }
      setDiffOpen(false);
      onOpenChange(false);
    } catch (e) {
      setDiffOpen(false);
      if (e instanceof ApiError) {
        // Бэк 422 code-collision / 409 дубль — показываем как inline-ошибку имени.
        if (e.status === 422 || e.status === 409) {
          setNameError(e.detail);
        } else {
          setSubmitError(e.detail);
        }
      } else {
        setSubmitError('Не удалось сохранить роль');
      }
    }
  };

  const onSubmitClick = () => {
    if (!runNameValidation(name)) return;
    // Правка существующей роли → сперва диф-подтверждение. Создание — сразу.
    if (mode.kind === 'edit') {
      setDiffOpen(true);
    } else {
      void doSave();
    }
  };

  const saving = createMut.isPending || updateMut.isPending;
  const memberCount = membersQ.data?.member_count ?? 0;
  const loadingDetail = isEdit && detailQ.isLoading;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[640px]">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {mode.kind === 'duplicate-custom'
              ? `Копия роли «${mode.from.name}». Переименуйте и поправьте права.`
              : mode.kind === 'edit'
                ? 'Кастомная роль хозяйства. Меняйте, что роль может делать с каждым разделом.'
                : 'Создайте свою роль: имя, описание и права по разделам.'}
          </SheetDescription>

          {loadingDetail ? (
            <div className="mt-6 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="role-name">
                  Название роли <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="role-name"
                  value={name}
                  placeholder="например, Скаут-стажёр"
                  aria-invalid={!!nameError}
                  className={nameError ? 'border-destructive' : undefined}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) runNameValidation(e.target.value);
                  }}
                  onBlur={(e) => runNameValidation(e.target.value)}
                />
                {nameError ? (
                  <p className="text-xs text-destructive">{nameError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Имя не должно совпадать с системными ролями.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="role-desc">Описание · необязательно</Label>
                <textarea
                  id="role-desc"
                  value={description}
                  placeholder="Коротко: что делает эта роль."
                  onChange={(e) => setDescription(e.target.value)}
                  className="flex min-h-[56px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Права по разделам</Label>
                  <ScopeLegend />
                </div>
                <PermissionMatrix matrix={matrix} onChange={setMatrix} />
              </div>

              {submitError && (
                <Alert variant="destructive">
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <div className="mt-6 flex gap-3 border-t border-border pt-4">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Отмена
            </Button>
            <Button
              variant={mode.kind === 'edit' ? 'default' : 'accent-brand'}
              className="flex-1"
              onClick={onSubmitClick}
              disabled={saving || loadingDetail || !dirty}
            >
              {mode.kind === 'edit' ? 'Сохранить' : 'Создать роль'}
            </Button>
          </div>
          {mode.kind === 'edit' && (
            <p className="mt-2.5 text-center text-[11.5px] text-muted-foreground">
              Перед сохранением покажем, что изменится и кого затронет.
            </p>
          )}
        </SheetContent>
      </Sheet>

      {mode.kind === 'edit' && (
        <DiffConfirmDialog
          open={diffOpen}
          onOpenChange={setDiffOpen}
          roleName={name.trim() || detailQ.data?.name || 'роль'}
          lines={diffLines}
          memberCount={memberCount}
          saving={saving}
          onConfirm={doSave}
        />
      )}
    </>
  );
}
